import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, CaretLeft, CaretRight, ArrowRight, Flame, Play, Pause, CursorClick } from "@phosphor-icons/react";

const STEPS = [
  { img: "/shots/vozelto-ai-employees.jpg", title: "Meet your AI marketing team", tag: "Step 1 · Your Team", spot: { x: 50, y: 42 },
    desc: "Twelve AI specialists — a Marketing Director, Social Manager, Image Studio, SEO Manager, Ad Manager and more — each with a real job, working together on your brand." },
  { img: "/shots/vozelto-brand-assets.jpg", title: "Upload your brand once", tag: "Step 2 · Brand Assets", spot: { x: 34, y: 38 },
    desc: "Logos, colors, voice, taglines, hashtags and product photos live in one Brand Assets library. Every AI employee uses YOUR real branding — nothing is invented." },
  { img: "/shots/vozelto-templates.jpg", title: "Launch campaigns in one click", tag: "Step 3 · Templates", spot: { x: 62, y: 45 },
    desc: "Pick from ready-made campaign templates for Facebook, Instagram, Google, email and SMS. AI personalizes each one to your brand before it launches." },
  { img: "/shots/vozelto-image-studio.jpg", title: "AI creates on-brand visuals", tag: "Step 4 · Image Studio", spot: { x: 45, y: 55 },
    desc: "Product photography, ads and social creative — generated with your real logo and brand style, then scored by AI vision for quality before you ever see it." },
  { img: "/shots/vozelto-social-manager.jpg", title: "Social content, written and ready", tag: "Step 5 · Social Manager", spot: { x: 55, y: 40 },
    desc: "Captions, hashtags and creative for Facebook and Instagram — drafted in your voice and queued for your review, then published directly to your pages." },
  { img: "/shots/vozelto-approval-queue.jpg", title: "You approve everything", tag: "Step 6 · Approval Queue", spot: { x: 70, y: 35 },
    desc: "Nothing publishes without your sign-off. Each design gets a 100-point Campaign Readiness Score — and one click asks the AI to improve it before launch." },
  { img: "/shots/vozelto-marketing-calendar.jpg", title: "See your whole month at a glance", tag: "Step 7 · Marketing Calendar", spot: { x: 48, y: 50 },
    desc: "Every approved post, email and campaign lands on one calendar, scheduled and organized — your marketing runs like clockwork." },
  { img: "/shots/vozelto-executive-dashboard.jpg", title: "Watch the results roll in", tag: "Step 8 · Command Center", spot: { x: 30, y: 32 },
    desc: "Revenue, traffic, ad performance and AI recommendations in one executive view. Your AI team reports back — you stay in command." },
];

const AUTO_STEP_MS = 5000;

export default function InteractiveDemo({ open, onClose, autoPlay = false }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(false);
  useEffect(() => { if (open) { setI(0); setAuto(autoPlay); } }, [open, autoPlay]);
  const next = useCallback(() => { setAuto(false); setI((v) => Math.min(v + 1, STEPS.length - 1)); }, []);
  const prev = useCallback(() => { setAuto(false); setI((v) => Math.max(v - 1, 0)); }, []);
  useEffect(() => {
    if (!open || !auto) return;
    const t = setInterval(() => {
      setI((v) => {
        if (v >= STEPS.length - 1) { setAuto(false); return v; }
        return v + 1;
      });
    }, AUTO_STEP_MS);
    return () => clearInterval(t);
  }, [open, auto]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);
  if (!open) return null;
  const s = STEPS[i];
  const last = i === STEPS.length - 1;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6" data-testid="interactive-demo">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl card-matte brand-glow overflow-hidden rounded-2xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/8">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Flame size={16} className="brand-text" weight="fill" /> Vozelto Interactive Demo
            <span className="hidden sm:inline text-[11px] text-[#7a7a7a] font-normal ml-1">· 2 minutes, no account needed</span>
          </div>
          <div className="flex items-center gap-2">
            {auto && !last && (
              <button data-testid="demo-skip" onClick={() => { setAuto(false); setI(STEPS.length - 1); }}
                className="h-8 rounded-full px-3 flex items-center gap-1 text-[11px] font-semibold text-[#9a9a9a] border border-white/10 hover:text-white hover:bg-white/8 transition-colors">
                Skip <CaretRight size={11} weight="bold" />
              </button>
            )}
            <button data-testid="demo-autoplay-toggle" onClick={() => setAuto((a) => { if (!a && last) setI(0); return !a; })}
              className={`h-8 rounded-full px-3 flex items-center gap-1.5 text-[11px] font-semibold border transition-colors ${auto ? "bg-[#6B3DFF]/15 text-[#A56CFF] border-[#6B3DFF]/30" : "text-[#9a9a9a] border-white/10 hover:text-white hover:bg-white/8"}`}>
              {auto ? <><Pause size={12} weight="fill" /> Pause</> : last ? <><Play size={12} weight="fill" /> Replay</> : <><Play size={12} weight="fill" /> Auto Play</>}
            </button>
            <button data-testid="demo-close" onClick={onClose} className="h-8 w-8 rounded-lg flex items-center justify-center text-[#9a9a9a] hover:text-white hover:bg-white/8"><X size={16} /></button>
          </div>
        </div>

        <div className="grid md:grid-cols-5">
          <div className="md:col-span-3 bg-[#0c0c0c] p-3 sm:p-4">
            <div className="relative">
              <img key={s.img + i} src={s.img} alt={s.title} width="1600" height="900"
                className="w-full rounded-lg border border-white/8 demo-fade" data-testid="demo-screenshot" />
              {auto && (
                <>
                  <div data-testid="demo-highlight-ring" className="demo-highlight pointer-events-none"
                    style={{ left: `${s.spot.x}%`, top: `${s.spot.y}%` }} />
                  <div data-testid="demo-cursor" className="demo-cursor pointer-events-none"
                    style={{ left: `${s.spot.x}%`, top: `${s.spot.y}%` }}>
                    <CursorClick size={22} weight="fill" className="text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg overflow-hidden">
                    <div key={i} className="demo-autobar h-full bg-[#6B3DFF]" style={{ animationDuration: `${AUTO_STEP_MS}ms` }} />
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="md:col-span-2 p-5 sm:p-7 flex flex-col">
            <div className="label-caps brand-text mb-2" data-testid="demo-step-tag">{s.tag}</div>
            <h3 className="font-head font-bold text-xl sm:text-2xl leading-tight" data-testid="demo-step-title">{s.title}</h3>
            <p className="mt-3 text-sm text-[#b5b5b5] leading-relaxed">{s.desc}</p>
            <div className="mt-auto pt-6">
              <div className="flex items-center gap-1.5 mb-4" data-testid="demo-progress">
                {STEPS.map((_, k) => (
                  <button key={k} onClick={() => { setAuto(false); setI(k); }} aria-label={`Step ${k + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${k === i ? "w-7 bg-[#6B3DFF]" : "w-3 bg-white/15 hover:bg-white/30"}`} />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="demo-prev" variant="ghost" onClick={prev} disabled={i === 0}
                  className="rounded-full border border-white/10 text-white hover:bg-white/5 h-10 px-4"><CaretLeft size={15} /> Back</Button>
                {last ? (
                  <Button data-testid="demo-start-trial" onClick={() => navigate("/signup")}
                    className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5 flex-1">
                    Start Free Trial <ArrowRight size={15} className="ml-1.5" weight="bold" />
                  </Button>
                ) : (
                  <Button data-testid="demo-next" onClick={next}
                    className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5 flex-1">
                    Next <CaretRight size={15} className="ml-1" weight="bold" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const WatchDemoButton = ({ onOpen, testid = "watch-demo-btn", className = "", label = "Watch Demo" }) => (
  <Button data-testid={testid} variant="ghost" onClick={onOpen}
    className={`text-white hover:bg-white/5 rounded-full px-6 h-12 text-base border border-white/10 ${className}`}>
    <Play size={16} className="mr-2 brand-text" weight="fill" /> {label}
  </Button>
);
